import { useState, useRef } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { createFeedbackItem, uploadFeedbackAttachment } from "@/lib/api";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Paperclip, X, Image, FileText } from "lucide-react";

interface FeedbackSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const FEEDBACK_TYPES = [
  { value: "feature_request", label: "Feature Request" },
  { value: "bug",             label: "Bug Report" },
  { value: "ux",              label: "UX / Workflow" },
  { value: "look_and_feel",   label: "Look & Feel" },
  { value: "performance",     label: "Performance" },
  { value: "other",           label: "Other" },
];

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function FeedbackSheet({ open, onOpenChange }: FeedbackSheetProps) {
  const [type, setType] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [uploadProgress, setUploadProgress] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const reset = () => {
    setType("");
    setTitle("");
    setDescription("");
    setFiles([]);
    setUploadProgress("");
  };

  const submitMutation = useMutation({
    mutationFn: async () => {
      // 1. Create the feedback item
      const item = await createFeedbackItem({ type, title, description: description || undefined });

      // 2. Upload any attached files
      if (files.length > 0) {
        for (let i = 0; i < files.length; i++) {
          setUploadProgress(`Uploading file ${i + 1} of ${files.length}…`);
          await uploadFeedbackAttachment(item.id, files[i]);
        }
      }

      return item;
    },
    onSuccess: () => {
      toast({ title: "Feedback submitted", description: "Thanks! We'll review it soon." });
      queryClient.invalidateQueries({ queryKey: ["feedback"] });
      reset();
      onOpenChange(false);
    },
    onError: (err: any) => {
      setUploadProgress("");
      toast({ title: "Failed to submit", description: err.message, variant: "destructive" });
    },
  });

  const addFiles = (fileList: FileList | null) => {
    if (!fileList) return;
    setFiles((prev) => [...prev, ...Array.from(fileList)]);
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    const items = Array.from(e.clipboardData.items);
    const imageItems = items.filter((item) => item.kind === "file" && item.type.startsWith("image/"));
    if (imageItems.length === 0) return;
    const pastedFiles = imageItems.map((item) => {
      const file = item.getAsFile()!;
      return new File([file], `screenshot-${Date.now()}.png`, { type: file.type });
    });
    setFiles((prev) => [...prev, ...pastedFiles]);
  };

  const removeFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const canSubmit = type && title.trim().length > 0;

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!submitMutation.isPending) { reset(); onOpenChange(v); } }}>
      <SheetContent side="right" className="w-[420px] sm:w-[460px] flex flex-col">
        <SheetHeader>
          <SheetTitle>Submit Feedback</SheetTitle>
        </SheetHeader>

        <div className="flex flex-col gap-4 mt-4 flex-1 overflow-y-auto pr-1">
          <div className="space-y-1.5">
            <Label htmlFor="fb-type">Category</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger id="fb-type">
                <SelectValue placeholder="What kind of feedback?" />
              </SelectTrigger>
              <SelectContent>
                {FEEDBACK_TYPES.map((t) => (
                  <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="fb-title">Title</Label>
            <Input
              id="fb-title"
              placeholder="Brief summary…"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="fb-desc">
              Details <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Textarea
              id="fb-desc"
              placeholder="Describe the issue or idea… (you can also paste a screenshot here)"
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onPaste={handlePaste}
            />
          </div>

          {/* Attachments */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>
                Attachments <span className="text-muted-foreground font-normal">(optional)</span>
              </Label>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                <Paperclip className="h-3.5 w-3.5" />
                Add file
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,.pdf,.doc,.docx,.txt,.csv,.xlsx"
                className="hidden"
                onChange={(e) => addFiles(e.target.files)}
              />
            </div>

            {files.length > 0 && (
              <div className="space-y-1.5">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 rounded border bg-muted/30 text-sm">
                    {f.type.startsWith("image/")
                      ? <Image className="h-4 w-4 text-muted-foreground shrink-0" />
                      : <FileText className="h-4 w-4 text-muted-foreground shrink-0" />}
                    <span className="flex-1 truncate text-xs">{f.name}</span>
                    <span className="text-xs text-muted-foreground shrink-0">{formatBytes(f.size)}</span>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="text-muted-foreground hover:text-destructive transition-colors"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {files.length === 0 && (
              <p className="text-xs text-muted-foreground">
                Screenshots are especially helpful for bug reports. PNG, JPG, PDF up to 10 MB.
              </p>
            )}
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t mt-2">
          <Button
            variant="outline"
            onClick={() => { reset(); onOpenChange(false); }}
            disabled={submitMutation.isPending}
          >
            Cancel
          </Button>
          <Button onClick={() => submitMutation.mutate()} disabled={!canSubmit || submitMutation.isPending}>
            {submitMutation.isPending && (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            )}
            {uploadProgress || "Submit"}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
